import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod/v3";
import { useResetPassword } from "@/api/auth/reset-password";
import { Button } from "@/components/base/buttons/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { resetPasswordRoute } from "@/router";

const formSchema = z.object({
  email: z.string(),
  newPassword: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/[a-zA-Z]/, { message: "Password must contain at least one letter" })
    .regex(/\d/, { message: "Password must contain at least one number" })
    .regex(/[@#!$%&*_]/, { message: "Password must contain at least one special character" }),
});
export const ResetPasswordPage = () => {
  const { email, token } = resetPasswordRoute.useSearch();
  const form = useForm({ resolver: zodResolver(formSchema), defaultValues: { email } });
  const { mutateAsync, error, isPending: loading, data } = useResetPassword();
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<"valid" | "invalid">();
  const navigate = useNavigate();

  const handleSubmit = async (payload: z.infer<typeof formSchema>) => {
    const { status } = await mutateAsync({ ...payload, token: token ?? "" });
    setStatus(status);
    if (status === "valid") {
      setTimeout(() => {
        navigate({ to: "/login", search: { email: form.getValues("email") } });
      }, 2000);
    }
  };

  if (status === "valid") {
    return (
      <div className="fixed w-full h-full left-0 top-0 bg-white z-50" data-testid="reset-password-success-page">
        <div className="flex min-h-screen items-center justify-center bg-white p-4">
          <Card className="w-full max-w-md" data-testid="success-card">
            <CardHeader className="space-y-1">
              <div className="mb-4 flex items-center justify-center">
                <div className="w-1/2 px-2">
                  <img src="/logo.png" alt="Unbox Logo" />
                </div>
              </div>
              <CardTitle className="text-center text-2xl font-bold text-gray-800">Password Reset Successful</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <Alert variant="default">
                <AlertDescription>Your password has been reset successfully. Redirecting to login...</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (status === "invalid") {
    return (
      <div className="fixed w-full h-full left-0 top-0 bg-white z-50" data-testid="reset-password-invalid-page">
        <div className="flex min-h-screen items-center justify-center bg-white p-4">
          <Card className="w-full max-w-md" data-testid="success-card">
            <CardHeader className="space-y-1">
              <div className="mb-4 flex items-center justify-center">
                <div className="w-1/2 px-2">
                  <img src="/logo.png" alt="Unbox Logo" />
                </div>
              </div>
              <CardTitle className="text-center text-2xl font-bold text-gray-800">Password Reset Failed</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <Alert variant="default">
                <AlertDescription>
                  {data?.message ?? "An unexpected error occured when resetting your password. Please try again later."}
                </AlertDescription>
              </Alert>

              <Button type="button" color="secondary" className="w-full" href="/login">
                Back to log in
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed w-full h-full left-0 top-0 bg-white z-50" data-testid="reset-password-page">
      <div className="flex min-h-screen items-center justify-center bg-white p-4">
        <Card className="w-full max-w-md" data-testid="reset-password-form">
          <CardHeader className="space-y-1">
            <div className="mb-4 flex items-center justify-center">
              <div className="w-1/2 px-2">
                <img src="/logo.png" alt="Unbox Logo" />
              </div>
            </div>
            <CardTitle className="text-center text-2xl font-bold text-gray-800">Reset Your Password</CardTitle>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label htmlFor="email">Email address</Label>
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter your email"
                        required
                        disabled={loading}
                        className="w-full"
                        autoComplete="username"
                        data-testid="email-input"
                      />
                    </div>
                  )}
                />
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <div className="space-y-2">
                      <Label htmlFor="password">New Password</Label>
                      <div className="relative">
                        <Input
                          {...field}
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your new password"
                          required
                          disabled={loading}
                          className="w-full pr-10"
                          autoComplete="new-password"
                          minLength={8}
                          data-testid="password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 transform text-gray-500 hover:text-gray-700"
                          disabled={loading}
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>

                      {form.formState.errors.newPassword ? (
                        <p className="text-xs text-red-400 -mt-1" data-testid="password-error">
                          {form.formState.errors.newPassword.message}
                        </p>
                      ) : (
                        <p className="text-sm text-gray-500">Password must be at least 8 characters long</p>
                      )}
                    </div>
                  )}
                />
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error.message}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full" isDisabled={loading} data-testid="submit-button">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
                <div className="flex gap-2">
                  <Button
                    href="/auth/forgot-password"
                    type="button"
                    color="secondary"
                    className="flex-1"
                    isDisabled={loading}
                  >
                    Resend Code
                  </Button>
                  <Button href="/login" type="button" color="tertiary" className="flex-1" isDisabled={loading}>
                    Back to log in
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
